import { Link } from '@tanstack/react-router'
import { CheckCircle2, ShieldCheck, UserCheck } from 'lucide-react'
import type { ReactNode } from 'react'

import type { DualControlResult } from '@/api/types'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'

/**
 * Dual control, said before the click and again afterwards.
 *
 * Five of the writes this area makes answer with a {@link DualControlResult} rather than the thing
 * that was asked for: closing a shift, moving vault cash, creating a till, creating a vault, and
 * paying a savings withdrawal. All three of its members are always present, and two of them are
 * nullable in a way that matters — `approvalRequestId` is set and `entityId` null on the deferred
 * path, and exactly the reverse when the action executed. Reading either without checking
 * `awaitingApproval` first is how a screen reports success for a request that is sitting in a queue.
 */

export function DualControlNotice({
  action,
  approvePermission,
  canCountersign,
  /**
   * Set where the maker and the checker cannot be the same person by construction — not merely
   * "you do not hold it", but "nobody who does this work can hold it". The segregation matrix
   * declares `teller.session.open` and `teller.eod.submit.approve` an incompatible pair, so a close
   * always waits for someone else, and offering the usual hedge there would be a lie.
   */
  barredPair = false,
  children,
}: {
  action: string
  approvePermission: string
  canCountersign: boolean
  barredPair?: boolean
  children?: ReactNode
}) {
  if (barredPair) {
    return (
      <Alert tone="warning">
        <UserCheck aria-hidden />
        <div className="space-y-1">
          <AlertTitle>{action} always waits for a supervisor</AlertTitle>
          <AlertDescription>
            <span className="font-mono text-xs">{approvePermission}</span> cannot be held by anyone
            who opens a till — the two are an incompatible pair in the segregation matrix, checked
            when a role is assigned. Confirming this records the declaration and stops the drawer
            trading; the shift closes when a supervisor countersigns.
            {children}
          </AlertDescription>
        </div>
      </Alert>
    )
  }

  return (
    <Alert tone={canCountersign ? 'info' : 'warning'}>
      {canCountersign ? <ShieldCheck aria-hidden /> : <UserCheck aria-hidden />}
      <div className="space-y-1">
        <AlertTitle>{action} is countersigned</AlertTitle>
        <AlertDescription>
          {/*
            What decides this is the branch's approval policy at this amount, and nothing else.
            `ApprovalService.SubmitForApprovalAsync` resolves the policy, executes at once where
            none applies, and otherwise stores the request — it never consults what the person
            submitting it holds, and the engine refuses an approval from the maker. So holding the
            countersigning permission is worth saying (it is what lets this person release somebody
            else's request) but it is not a reason to expect this one to complete now, and a dialog
            that promised otherwise would have a teller handing cash over on the strength of it.
          */}
          Whether this takes effect now or waits is decided by the branch&apos;s approval policy at
          this amount, not by what you hold: a request the policy covers is stored, and you cannot
          countersign your own.{' '}
          {canCountersign ? (
            <>
              Your role does hold <span className="font-mono text-xs">{approvePermission}</span>,
              which is what lets you release someone else&apos;s.
            </>
          ) : (
            <>
              Your role does not hold <span className="font-mono text-xs">{approvePermission}</span>,
              so somebody who does has to release it. This dialog says which of the two happened.
            </>
          )}
          {children}
        </AlertDescription>
      </div>
    </Alert>
  )
}

/**
 * What actually happened.
 *
 * `awaitingApproval: true` means the command is stored and no cash has moved, so the wording is
 * "sent for countersignature" and never "done". The request id is shown because it is what somebody
 * quotes when they ring the approver.
 */
export function DualControlOutcome({
  result,
  awaitingDescription,
  appliedDescription,
  deferredWarning,
}: {
  result: DualControlResult
  /** What is now waiting, in one line. */
  awaitingDescription: string
  /** What has now changed, in one line. */
  appliedDescription: string
  /** Anything the deferral itself makes true — a check that will only run at approval time. */
  deferredWarning?: ReactNode
}) {
  if (result.awaitingApproval) {
    return (
      <Alert tone="warning">
        <UserCheck aria-hidden />
        <div className="space-y-1">
          <AlertTitle>Sent for countersignature — no cash has moved</AlertTitle>
          <AlertDescription>
            {awaitingDescription} Tell the customer after it has been countersigned, not now.
            {result.approvalRequestId ? (
              <>
                {' '}
                Request <span className="font-mono text-xs">{result.approvalRequestId}</span>.
              </>
            ) : null}
            {deferredWarning ? <div className="mt-1.5">{deferredWarning}</div> : null}
          </AlertDescription>
          <Button asChild variant="secondary" size="sm">
            <Link to="/approvals" search={{ tab: 'mine' }}>
              Track it under Submitted by me
            </Link>
          </Button>
        </div>
      </Alert>
    )
  }

  return (
    <Alert tone="success">
      <CheckCircle2 aria-hidden />
      <div className="space-y-1">
        <AlertTitle>Done</AlertTitle>
        <AlertDescription>
          {appliedDescription}
          {/* Present only on the executed path, and it is the identity of the thing created. */}
          {result.entityId ? (
            <>
              {' '}
              Reference <span className="font-mono text-xs">{result.entityId}</span>.
            </>
          ) : null}
        </AlertDescription>
      </div>
    </Alert>
  )
}
